import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus,
  Upload,
  RefreshCw,
  Users,
  List,
  ArrowLeft,
  Download,
  Trash2,
  X,
  Shuffle,
} from "lucide-react";
import { apiUrl } from "./LoginSignup";
import Loader from "./Loader";
import "./LeadCampaigns.css";

const LeadCampaigns = () => {
  const userSession = useMemo(
    () => JSON.parse(localStorage.getItem("userSession")) || {},
    []
  );
  const isLeadManager = userSession.user_role === "srdev";
  const authHeaders = useMemo(
    () => ({ authorization: userSession.token }),
    [userSession.token]
  );

  const [view, setView] = useState("list");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignLeads, setCampaignLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [bdmList, setBdmList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [importResult, setImportResult] = useState(null);

  const [createModal, setCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    scheme: "",
    source: "",
    auto_assign: false,
    round_robin_bdm_ids: [],
  });
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    scheme: "",
    source: "",
    auto_assign: false,
    round_robin_bdm_ids: [],
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      setListLoading(true);
      const res = await axios.get(`${apiUrl}/leads/campaigns`, { headers: authHeaders });
      setCampaigns(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setCampaigns([]);
      setError(err.response?.data?.message || "Failed to load campaigns");
    } finally {
      setListLoading(false);
    }
  }, [authHeaders]);

  const fetchBdmList = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/user/bdm-list`, { headers: authHeaders });
      setBdmList(res.data || []);
    } catch {
      setBdmList([]);
    }
  }, [authHeaders]);

  const fetchCampaignDetail = useCallback(
    async (id) => {
      try {
        const res = await axios.get(`${apiUrl}/leads/campaigns/${id}`, { headers: authHeaders });
        const campaign = res.data;
        setSelectedCampaign(campaign);
        setEditForm({
          name: campaign.name || "",
          description: campaign.description || "",
          scheme: campaign.scheme || "",
          source: campaign.source || "",
          auto_assign: Boolean(campaign.auto_assign),
          round_robin_bdm_ids: campaign.round_robin_bdm_ids || [],
        });
        return campaign;
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load campaign");
        return null;
      }
    },
    [authHeaders]
  );

  const fetchCampaignLeads = useCallback(
    async (id, page = 1) => {
      try {
        const res = await axios.get(`${apiUrl}/leads/campaigns/${id}/leads`, {
          headers: authHeaders,
          params: { page, limit: 20 },
        });
        setCampaignLeads(res.data?.data || []);
        setPagination(
          res.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 }
        );
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load campaign leads");
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    if (!isLeadManager) {
      setListLoading(false);
      return;
    }
    fetchCampaigns();
    fetchBdmList();
  }, [isLeadManager, fetchCampaigns, fetchBdmList]);

  const openCampaign = async (campaign) => {
    setView("detail");
    setSelectedCampaign(campaign);
    setEditForm({
      name: campaign.name || "",
      description: campaign.description || "",
      scheme: campaign.scheme || "",
      source: campaign.source || "",
      auto_assign: Boolean(campaign.auto_assign),
      round_robin_bdm_ids: campaign.round_robin_bdm_ids || [],
    });
    setError("");
    setSuccess("");
    setImportResult(null);
    setDetailLoading(true);
    try {
      await fetchCampaignDetail(campaign.id);
      await fetchCampaignLeads(campaign.id, 1);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!createForm.name.trim()) {
      setError("Campaign name is required");
      return;
    }
    if (createForm.auto_assign && !createForm.round_robin_bdm_ids.length) {
      setError("Select at least one BDM for auto round-robin, or turn off auto-assign");
      return;
    }

    const payload = {
      ...createForm,
      round_robin_bdm_ids: createForm.round_robin_bdm_ids.map((id) => String(id)),
    };

    setActionLoading(true);
    setError("");
    try {
      const res = await axios.post(`${apiUrl}/leads/campaigns`, payload, {
        headers: authHeaders,
      });
      setSuccess("Campaign created");
      setCreateModal(false);
      setCreateForm({
        name: "",
        description: "",
        scheme: "",
        source: "",
        auto_assign: false,
        round_robin_bdm_ids: [],
      });
      await fetchCampaigns();
      if (res.data?.campaign) openCampaign(res.data.campaign);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to create campaign";
      setError(msg);
      console.error("Create campaign failed:", err.response?.data || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveCampaign = async () => {
    if (!selectedCampaign?.id) return;
    if (editForm.auto_assign && !editForm.round_robin_bdm_ids.length) {
      setError("Select at least one BDM for auto round-robin assignment");
      return;
    }

    setActionLoading(true);
    setError("");
    try {
      const res = await axios.patch(
        `${apiUrl}/leads/campaigns/${selectedCampaign.id}`,
        editForm,
        { headers: authHeaders }
      );
      setSelectedCampaign(res.data.campaign);
      setSuccess("Campaign updated");
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update campaign");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign?.id) return;
    if (!window.confirm(`Delete campaign "${selectedCampaign.name}"?`)) return;

    setActionLoading(true);
    try {
      await axios.delete(`${apiUrl}/leads/campaigns/${selectedCampaign.id}`, {
        headers: authHeaders,
      });
      setSuccess("Campaign deleted");
      setView("list");
      setSelectedCampaign(null);
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete campaign");
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCampaign?.id) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setActionLoading(true);
    setError("");
    setSuccess("");
    setImportResult(null);

    try {
      const res = await axios.post(
        `${apiUrl}/leads/campaigns/${selectedCampaign.id}/import`,
        formData,
        {
          headers: {
            ...authHeaders,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setImportResult(res.data);
      setSuccess(
        `Import done: ${res.data.imported} new, ${res.data.duplicates_in_db || 0} existing in DB, ${res.data.duplicates_in_file || 0} duplicates in file`
      );
      await fetchCampaignDetail(selectedCampaign.id);
      await fetchCampaignLeads(selectedCampaign.id, 1);
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.message || "Bulk upload failed");
    } finally {
      setUploading(false);
      setActionLoading(false);
      e.target.value = "";
    }
  };

  const handleRoundRobin = async () => {
    if (!selectedCampaign?.id) return;

    setActionLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${apiUrl}/leads/campaigns/${selectedCampaign.id}/assign-round-robin`,
        {},
        { headers: authHeaders }
      );
      setSuccess(res.data.message || "Round-robin assignment completed");
      await fetchCampaignDetail(selectedCampaign.id);
      await fetchCampaignLeads(selectedCampaign.id, pagination.page);
      fetchCampaigns();
    } catch (err) {
      setError(err.response?.data?.message || "Round-robin assignment failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${apiUrl}/leads/campaigns/import-template`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "lead_import_template.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download template");
    }
  };

  const toggleBdm = (formSetter, bdmId) => {
    const id = String(bdmId);
    formSetter((prev) => ({
      ...prev,
      round_robin_bdm_ids: prev.round_robin_bdm_ids.map(String).includes(id)
        ? prev.round_robin_bdm_ids.filter((x) => String(x) !== id)
        : [...prev.round_robin_bdm_ids.map(String), id],
    }));
  };

  const BdmCheckboxes = ({ selectedIds, onToggle }) => (
    <div className="campaign-bdm-grid">
      {bdmList.map((bdm) => (
        <label key={bdm._id} className="campaign-bdm-chip">
          <input
            type="checkbox"
            checked={selectedIds.map(String).includes(String(bdm._id))}
            onChange={() => onToggle(bdm._id)}
          />
          {bdm.name} ({bdm.user_role})
        </label>
      ))}
    </div>
  );

  if (!isLeadManager) {
    return (
      <div className="lead-campaigns">
        <div className="alert alert-error">
          Access denied. Lead Campaigns are available for SRDEV only.
        </div>
      </div>
    );
  }

  return (
    <div className="lead-campaigns">
      <div className="page-header">
        <div>
          <h1>Lead Campaigns</h1>
          <p className="page-subtitle">
            Create lead lists, bulk upload, and auto-assign via round-robin
          </p>
        </div>
        {view === "list" ? (
          <button type="button" className="btn btn-primary" onClick={() => setCreateModal(true)}>
            <Plus size={16} /> New Campaign
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setView("list");
              setSelectedCampaign(null);
              setImportResult(null);
            }}
          >
            <ArrowLeft size={16} /> Back to List
          </button>
        )}
      </div>

      {(listLoading || detailLoading) && (
        <div className="campaign-loading-banner">Loading...</div>
      )}

      {error && (
        <div className="alert alert-error">
          {error}
          <button type="button" className="action-link" onClick={() => setError("")}>
            Dismiss
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button type="button" className="action-link" onClick={() => setSuccess("")}>
            Dismiss
          </button>
        </div>
      )}

      {view === "list" && (
        <div className="campaign-grid">
          {campaigns.length === 0 ? (
            <div className="campaign-empty">
              <List size={40} />
              <p>No campaigns yet. Create one to start bulk uploading leads.</p>
            </div>
          ) : (
            campaigns.map((c) => (
              <button
                key={c.id}
                type="button"
                className="campaign-card"
                onClick={() => openCampaign(c)}
              >
                <div className="campaign-card-title">{c.name}</div>
                {c.scheme && <div className="campaign-card-meta">Scheme: {c.scheme}</div>}
                {c.source && <div className="campaign-card-meta">Source: {c.source}</div>}
                <div className="campaign-card-stats">
                  <span>{c.stats?.total_leads || 0} leads</span>
                  <span>{c.stats?.unassigned_leads || 0} unassigned</span>
                </div>
                {c.auto_assign && (
                  <span className="campaign-badge">Auto round-robin ON</span>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {view === "detail" && selectedCampaign && (
        <div className={`campaign-detail${detailLoading ? " is-loading" : ""}`}>
          <div className="campaign-detail-grid">
            <section className="campaign-panel">
              <h2>Campaign Settings</h2>
              <div className="form-group">
                <label>Name</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Default Scheme</label>
                  <input
                    value={editForm.scheme}
                    onChange={(e) => setEditForm({ ...editForm, scheme: e.target.value })}
                    placeholder="e.g. pmegp"
                  />
                </div>
                <div className="form-group">
                  <label>Default Source</label>
                  <input
                    value={editForm.source}
                    onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    placeholder="e.g. meta, bulk_upload"
                  />
                </div>
              </div>

              <label className="campaign-toggle">
                <input
                  type="checkbox"
                  checked={editForm.auto_assign}
                  onChange={(e) =>
                    setEditForm({ ...editForm, auto_assign: e.target.checked })
                  }
                />
                Auto-assign new uploads via round-robin
              </label>

              <div className="form-group">
                <label>
                  <Users size={14} /> Round-robin BDM pool
                </label>
                <BdmCheckboxes
                  selectedIds={editForm.round_robin_bdm_ids}
                  onToggle={(id) => toggleBdm(setEditForm, id)}
                />
              </div>

              <div className="campaign-actions-row">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveCampaign}
                  disabled={actionLoading}
                >
                  Save Settings
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleRoundRobin}
                  disabled={actionLoading}
                >
                  <Shuffle size={14} /> Run Round-Robin Now
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteCampaign}
                  disabled={actionLoading}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>

              <div className="campaign-stats-row">
                <div className="stat-pill">
                  <strong>{selectedCampaign.stats?.total_leads || 0}</strong> Total
                </div>
                <div className="stat-pill">
                  <strong>{selectedCampaign.stats?.assigned_leads || 0}</strong> Assigned
                </div>
                <div className="stat-pill highlight">
                  <strong>{selectedCampaign.stats?.unassigned_leads || 0}</strong> Unassigned
                </div>
              </div>
            </section>

            <section className="campaign-panel">
              <h2>Bulk Upload</h2>
              <p className="panel-hint">
                Upload Excel (.xlsx) or CSV. Required column: <strong>Number</strong>. Duplicates
                (same phone, Meta Lead ID, or email) are never inserted twice — existing leads are
                linked to this campaign only.
              </p>
              <div className="upload-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleDownloadTemplate}
                  disabled={uploading}
                >
                  <Download size={14} /> Download Template
                </button>
                <label
                  className={`btn btn-primary btn-sm upload-label${uploading ? " disabled" : ""}`}
                  aria-disabled={uploading}
                >
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload File"}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    hidden
                    disabled={uploading}
                  />
                </label>
              </div>

              {uploading && (
                <div className="upload-loader" role="status" aria-live="polite">
                  <Loader />
                  <p>Uploading leads… please wait</p>
                </div>
              )}

              {importResult && !uploading && (
                <div className="import-result">
                  <div>New leads imported: {importResult.imported}</div>
                  <div>Already in database (skipped insert): {importResult.duplicates_in_db || 0}</div>
                  <div>Duplicate rows in file (skipped insert): {importResult.duplicates_in_file || 0}</div>
                  <div>Linked to campaign: {importResult.linked_existing}</div>
                  <div>Skipped (errors): {importResult.skipped}</div>
                  {importResult.assignment && (
                    <div>Auto-assigned: {importResult.assignment.created}</div>
                  )}
                  {importResult.errors?.length > 0 && (
                    <details>
                      <summary>{importResult.errors.length} row issue(s)</summary>
                      <ul>
                        {importResult.errors.slice(0, 10).map((e, i) => (
                          <li key={i}>
                            Row {e.row || e.leadId}: {e.message}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </section>
          </div>

          <section className="campaign-panel campaign-leads-panel">
            <div className="panel-header-row">
              <h2>Leads in Campaign</h2>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => fetchCampaignLeads(selectedCampaign.id, pagination.page)}
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>

            <div className="table-wrap">
              <table className="campaign-leads-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Number</th>
                    <th>Status</th>
                    <th>Scheme</th>
                    <th>Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignLeads.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-cell">
                        No leads in this campaign yet. Upload a file to get started.
                      </td>
                    </tr>
                  ) : (
                    campaignLeads.map((lead) => (
                      <tr key={lead.id}>
                        <td>{lead.id}</td>
                        <td>{lead.name}</td>
                        <td>{lead.number}</td>
                        <td>{lead.status}</td>
                        <td>{lead.scheme || "—"}</td>
                        <td>
                          <span
                            className={`assign-badge ${
                              lead.is_assigned ? "assigned" : "unassigned"
                            }`}
                          >
                            {lead.is_assigned ? "Yes" : "No"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination-row">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchCampaignLeads(selectedCampaign.id, pagination.page - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchCampaignLeads(selectedCampaign.id, pagination.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {createModal && (
        <div className="modal-overlay" onClick={() => setCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Lead Campaign</h2>
              <button type="button" className="action-link modal-close" onClick={() => setCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="e.g. PMEGP March 2026"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Scheme</label>
                  <input
                    value={createForm.scheme}
                    onChange={(e) => setCreateForm({ ...createForm, scheme: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <input
                    value={createForm.source}
                    onChange={(e) => setCreateForm({ ...createForm, source: e.target.value })}
                  />
                </div>
              </div>
              <label className="campaign-toggle">
                <input
                  type="checkbox"
                  checked={createForm.auto_assign}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, auto_assign: e.target.checked })
                  }
                />
                Auto-assign uploads via round-robin
              </label>
              <div className="form-group">
                <label>Round-robin BDMs</label>
                <BdmCheckboxes
                  selectedIds={createForm.round_robin_bdm_ids}
                  onToggle={(id) => toggleBdm(setCreateForm, id)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setCreateModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateCampaign}
                disabled={actionLoading}
              >
                {actionLoading ? "Creating..." : "Create Campaign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadCampaigns;
